/**
 * Seed create-wizard curriculum from Mongo course_live_session_plans.
 * Topics/subtopics are fixed; trainers only set subtopic durations + schedule.
 */

import { authService } from '../../../utils/authService';
import type { Topic } from '../types/curriculumTopic';
import type { SessionCategory } from './scheduledSessionStorage';
import {
  formatPeriodFromCourseName,
  instantiatePlannedSessionName,
} from './liveSessionNaming';

export type PlannedSessionTopic = {
  id?: string;
  name?: string;
  subtopics?: { id?: string; name?: string }[];
};

export type PlannedLiveSession = {
  id?: string;
  category?: string;
  title?: string;
  session_key?: string;
  sessionKey?: string;
  sequence?: number;
  duration_minutes?: number;
  durationMinutes?: number;
  topics?: PlannedSessionTopic[];
  workshop_day_count?: number;
  workshopDayCount?: number;
  day_topics?: PlannedSessionTopic[][];
  dayTopics?: PlannedSessionTopic[][];
};

export type CourseLiveSessionPlanFetch = {
  planId: string | null;
  sessions: PlannedLiveSession[];
};

function sessionKeyOf(session: PlannedLiveSession): string {
  return String(session.session_key || session.sessionKey || '').trim();
}

function mapPlanTopicsToCurriculum(
  topics: PlannedSessionTopic[] | undefined,
  workshopDayIndex?: number,
): Topic[] {
  const result: Topic[] = [];
  (topics || []).forEach((topic, topicIndex) => {
    const topicId = String(topic.id || '').trim() || `plan-topic-${topicIndex}`;
    const title = String(topic.name || '').trim();
    if (!title) return;
    const subtopics = (topic.subtopics || [])
      .map((sub, subIndex) => {
        const subId = String(sub.id || '').trim() || `${topicId}-sub-${subIndex}`;
        const subTitle = String(sub.name || '').trim();
        if (!subTitle) return null;
        return {
          id: subId,
          title: subTitle,
          durationMinutes: 0,
          selected: true,
        };
      })
      .filter(Boolean) as Topic['subtopics'];

    result.push({
      id: topicId,
      title,
      description: 'From course live session plan',
      durationMinutes: null,
      category: 'fundamentals',
      ...(workshopDayIndex != null ? { workshopDayIndex } : {}),
      ...(subtopics && subtopics.length > 0 ? { subtopics } : {}),
    });
  });
  return result;
}

/** Flatten plan session → Active Learning Path topics (all subtopics selected). */
export function plannedSessionToCurriculumTopics(
  session: PlannedLiveSession,
): Topic[] {
  const dayTopics = session.day_topics || session.dayTopics;
  if (Array.isArray(dayTopics) && dayTopics.length > 0) {
    const topics: Topic[] = [];
    dayTopics.forEach((day, dayIndex) => {
      topics.push(...mapPlanTopicsToCurriculum(day, dayIndex));
    });
    return topics;
  }
  return mapPlanTopicsToCurriculum(session.topics);
}

export function plannedSessionWorkshopDayCount(
  session: PlannedLiveSession,
): number {
  const fromField =
    Number(session.workshop_day_count ?? session.workshopDayCount) || 0;
  if (fromField > 0) return fromField;
  const dayTopics = session.day_topics || session.dayTopics;
  if (Array.isArray(dayTopics) && dayTopics.length > 0) return dayTopics.length;
  return 1;
}

export function plannedSessionDayTopicTitles(
  session: PlannedLiveSession,
): string[][] {
  const dayTopics = session.day_topics || session.dayTopics;
  if (Array.isArray(dayTopics) && dayTopics.length > 0) {
    return dayTopics.map((day) =>
      (day || [])
        .map((t) => String(t.name || '').trim())
        .filter(Boolean),
    );
  }
  const flat = (session.topics || [])
    .map((t) => String(t.name || '').trim())
    .filter(Boolean);
  return flat.length ? [flat] : [[]];
}

export function filterPlanSessionsByCategory(
  sessions: PlannedLiveSession[],
  category: SessionCategory,
): PlannedLiveSession[] {
  return (sessions || []).filter(
    (s) => String(s.category || '').trim() === category,
  );
}

export async function fetchCourseLiveSessionPlan(
  courseId: string,
  subjectId: string,
): Promise<CourseLiveSessionPlanFetch> {
  const cid = String(courseId || '').trim();
  const sid = String(subjectId || '').trim();
  if (!cid || !sid) return { planId: null, sessions: [] };
  try {
    const res = await authService.get(
      `admin/course_live_session_plan/${encodeURIComponent(cid)}/${encodeURIComponent(sid)}/`,
    );
    const data = res?.data || {};
    const sessions = Array.isArray(data.sessions) ? data.sessions : [];
    return {
      planId: data.plan_id ? String(data.plan_id) : null,
      sessions,
    };
  } catch (err) {
    console.warn('fetchCourseLiveSessionPlan failed', err);
    return { planId: null, sessions: [] };
  }
}

export function findPlannedSessionByKey(
  sessions: PlannedLiveSession[],
  key: string,
): PlannedLiveSession | undefined {
  const want = String(key || '').trim();
  if (!want) return undefined;
  return sessions.find((s) => sessionKeyOf(s) === want);
}

/** Session display title for a batch period (course name). */
export function resolveTitleFromPlannedSession(
  session: PlannedLiveSession,
  courseName: string,
): string {
  const template = String(session.title || '').trim();
  const period = formatPeriodFromCourseName(courseName);
  if (!template) return '';
  return period ? instantiatePlannedSessionName(template, period) : template;
}

export function plannedSessionLabel(session: PlannedLiveSession): string {
  const title = String(session.title || '').trim() || 'Planned session';
  const key = sessionKeyOf(session);
  const topicCount = (session.topics || []).length;
  return key ? `${title} (${topicCount} topics)` : title;
}

/** ``LCM PY - 001 - 7M 2026`` → ``LCM PY - 001`` for reuse checks. */
export function plannedSessionTitleBase(title: string): string {
  const text = String(title || '').trim();
  const match = text.match(/^(LCM\s+\S+\s+-\s+\d{3})\b/i);
  return match ? match[1] : text;
}

export function titleMatchesPlannedSession(
  existingTitle: string,
  planTitle: string,
): boolean {
  const existing = String(existingTitle || '').trim();
  const plan = String(planTitle || '').trim();
  if (!existing || !plan) return false;
  if (existing === plan) return true;
  if (existing.startsWith(`${plan} -`) || plan.startsWith(`${existing} -`)) {
    return true;
  }
  return plannedSessionTitleBase(existing) === plannedSessionTitleBase(plan);
}

/** True when this course-plan row was already applied for the batch. */
export function isPlannedSessionUsedByTitles(
  session: PlannedLiveSession,
  existingTitles: string[],
  courseName: string,
): boolean {
  const planTitle = String(session.title || '').trim();
  const resolved = resolveTitleFromPlannedSession(session, courseName);
  for (const title of existingTitles || []) {
    if (titleMatchesPlannedSession(title, planTitle)) return true;
    if (resolved && titleMatchesPlannedSession(title, resolved)) return true;
  }
  return false;
}
